import React, { useRef, useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

export default function ChartUploader({ imageBase64, chartUrl, caption, onImageChange, onUrlChange, onCaptionChange }) {
  const fileInputRef = useRef(null);
  const [mode, setMode] = useState(chartUrl && !imageBase64 ? 'url' : 'upload');
  const [localUrl, setLocalUrl] = useState(chartUrl || '');

  useEffect(() => {
    setLocalUrl(chartUrl || '');
  }, [chartUrl]);

  const processFile = (file) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        onImageChange(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUrlChange = (val) => {
    setLocalUrl(val);
    onUrlChange(val);
  };

  const hasImage = imageBase64 || chartUrl;

  return (
    <div className={`p-6 rounded-2xl border border-app-border bg-app-bg/30 ${hasImage ? 'mt-4' : 'mt-0'}`}>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <p className="text-[10px] font-black uppercase tracking-widest text-app-muted">
          {hasImage ? 'Update Chart Data' : 'Attach Analysis Output'}
        </p>
        <div className="flex bg-app-bg p-1 rounded-xl border border-app-border">
          <button 
            type="button"
            onClick={() => setMode('upload')}
            className={`px-4 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${mode === 'upload' ? 'bg-app-accent text-black shadow-lg' : 'text-app-muted'}`}
          >
            Upload
          </button>
          <button 
            type="button"
            onClick={() => setMode('url')}
            className={`px-4 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${mode === 'url' ? 'bg-app-accent text-black shadow-lg' : 'text-app-muted'}`}
          >
            Link
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {mode === 'upload' ? (
          <div className="relative">
            <div 
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-app-border rounded-xl p-6 flex flex-col items-center justify-center text-app-muted cursor-pointer hover:border-app-accent transition-all bg-app-surface/20 hover:bg-app-surface/40 group"
            >
              <SafeIcon icon={FiIcons.FiUploadCloud} className="text-2xl mb-2 opacity-50 group-hover:text-app-accent group-hover:opacity-100 transition-all" />
              <p className="text-[10px] font-black uppercase tracking-widest">
                {imageBase64 ? 'Replace Uploaded Chart' : 'Click to upload chart'}
              </p>
              <input type="file" ref={fileInputRef} onChange={e => processFile(e.target.files[0])} accept="image/*" className="hidden" />
            </div>
          </div>
        ) : (
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-app-accent z-10 pointer-events-none">
              <SafeIcon icon={FiIcons.FiLink} size={18} />
            </div>
            <input 
              type="text" 
              value={localUrl} 
              onChange={e => handleUrlChange(e.target.value)} 
              placeholder="Paste analysis URL (TradingView, Imgur, etc)..."
              className="w-full h-14 pl-12 pr-4 bg-app-surface/50 border-app-border focus:border-app-accent rounded-xl text-sm font-medium transition-all"
            />
          </div>
        )}
        
        <div className="relative">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-app-muted pointer-events-none">
            <FiIcons.FiType size={14} />
          </div>
          <input 
            type="text" 
            value={caption || ''} 
            onChange={e => onCaptionChange(e.target.value)} 
            placeholder="Add caption (e.g. 4H Breakout Structure)"
            className="w-full h-12 pl-12 pr-4 bg-app-surface/30 border-app-border rounded-xl text-[10px] font-bold uppercase tracking-widest text-app-text transition-all"
          />
        </div>
      </div>
    </div>
  );
}